/**
 * AddressChangeDialog — CIF-level streamlined address-change surface.
 *
 * Why this exists separately from "Edit CIF" (v0.1.44):
 *   The full PersonDialog surfaces every demographic field. Address change is
 *   by far the most-frequent CIF maintenance action at the branch — procedure
 *   `Address Verification.pdf` + `Change of Contact.pdf` describe a tight
 *   flow (new address + verification method + cascade to accounts + audit
 *   note) that officers want to complete in under 60 seconds. This dialog
 *   ports that flow verbatim.
 *
 * Fires from: CifDetail header strip ("Change Address" button).
 * Ops: custMod + acctMod (per cascaded account) + CustMsgAdd audit note.
 * Events: cif.address.changed, acct.address.cascaded, cif.field.changed.
 * Tasks: auto-create on out-of-state move.
 */

import { useEffect, useMemo, useState } from 'react';
import { Banner, Button, ModalShell } from '@/components/primitives';
import { useApi } from '../../hooks/useApi';
import { unwrapJxrlArray, txt } from '@/utils/jxrl';
import type { AcctType } from '@retail-ops/shared-types/enums';
import { useAddressChange } from './useAddressChange';
import type { MaintActionBaseProps } from '../types';

export interface AddressChangeDialogProps extends MaintActionBaseProps {
  /** Current address — prepopulates the "from" display. */
  currentAddress?: {
    addr?: string;
    addr2?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
}

interface CascadeOption {
  acctId: string;
  acctType: AcctType | string;
  prodDesc?: string;
  selected: boolean;
}

const VERIFICATION_METHODS: ReadonlyArray<{ code: string; label: string }> = [
  { code: '0', label: 'Post Office Verification' },
  { code: '1', label: 'Photo ID (In Person)' },
  { code: '2', label: 'SSN Verification' },
  { code: '3', label: 'Banno (Online Banking)' },
  { code: '4', label: 'Security Questions' },
  { code: '5', label: 'Transactional Questions' },
];

const ACCT_TYPE_LABEL: Record<string, string> = {
  '10': 'DDA', '20': 'SAV', '30': 'CD', '50': 'SDB', '60': 'IRA',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--ink-100)',
  borderRadius: 'var(--r-sm)',
  fontSize: 13,
  fontFamily: 'inherit',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--ink-600)',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  fontWeight: 600,
  marginBottom: 4,
  display: 'block',
};

export function AddressChangeDialog(props: AddressChangeDialogProps): JSX.Element | null {
  const { open, onClose, onSaved, cifId, officerCode = '000', readOnly = false, currentAddress } = props;
  const { client, makeSignal } = useApi();
  const { run } = useAddressChange();

  const [addr, setAddr] = useState<string>('');
  const [addr2, setAddr2] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [zip, setZip] = useState<string>('');
  const [verCode, setVerCode] = useState<string>('1');
  const [initials, setInitials] = useState<string>('');
  const [cascade, setCascade] = useState<ReadonlyArray<CascadeOption>>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset form when opened.
  useEffect(() => {
    if (!open) return;
    setAddr(currentAddress?.addr ?? '');
    setAddr2(currentAddress?.addr2 ?? '');
    setCity(currentAddress?.city ?? '');
    setState(currentAddress?.state ?? '');
    setZip(currentAddress?.zip ?? '');
    setVerCode('1');
    setInitials('');
    setErr(null);
  }, [open, currentAddress]);

  // Load cascade candidates when opened.
  useEffect(() => {
    if (!open) return;
    const signal = makeSignal();
    client.accounts.custAcctRelInq(cifId, { signal }).then(
      (res): void => {
        // camelCase {success,data} envelope; the typed PascalCase shape is wrong.
        const env = res as unknown as { success?: boolean };
        if (env.success === false) return;
        const raw = res as unknown as Record<string, unknown>;
        const dataNode =
          (raw['data'] as Record<string, unknown> | undefined) ??
          (raw['Data'] as Record<string, unknown> | undefined) ??
          raw;
        const recs = unwrapJxrlArray<Record<string, unknown>>(
          dataNode,
          'CustAcctRelInqRecArray',
          'CustAcctRelInqRec',
        );
        setCascade(
          recs
            .map((r) => {
              const acctObj = r['AcctId'];
              let acctId = '';
              let acctType = '';
              if (typeof acctObj === 'string') {
                acctId = acctObj;
              } else if (acctObj && typeof acctObj === 'object') {
                const inner = acctObj as Record<string, unknown>;
                acctId = txt(inner['AcctId']);
                acctType = txt(inner['AcctType']);
              }
              if (!acctType) acctType = txt(r['AcctType']);
              return { acctId, acctType }; // strings; cast at the cascade boundary
            })
            // Skip non-deposit relationship rows: 80=CIF link, 91=ATM/EFT card,
            // 92=NetTeller. These are not addressable at the acct-mod level.
            .filter((a) => Boolean(a.acctId) && Boolean(a.acctType)
              && a.acctType !== '80' && a.acctType !== '91' && a.acctType !== '92')
            .map((a) => ({
              acctId: a.acctId,
              acctType: a.acctType as AcctType,
              selected: true, // default: cascade to all deposit accounts
            })),
        );
      },
      (): void => {
        /* ignore — dialog still usable without cascade */
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cifId]);

  const outOfState = useMemo((): boolean => {
    return Boolean(state && currentAddress?.state && state !== currentAddress.state);
  }, [state, currentAddress?.state]);

  const canSave = useMemo((): boolean => {
    if (readOnly) return false;
    if (!addr.trim() || !city.trim() || !state.trim() || !zip.trim()) return false;
    if (state.length !== 2) return false;
    if (!/^\d{5}(-?\d{4})?$/.test(zip)) return false;
    if (verCode !== '0' && verCode !== '3' && initials.trim().length < 2) return false;
    return !submitting;
  }, [readOnly, addr, city, state, zip, verCode, initials, submitting]);

  const toggleCascade = (acctId: string): void => {
    setCascade((prev) =>
      prev.map((c) => (c.acctId === acctId ? { ...c, selected: !c.selected } : c)),
    );
  };

  const selectAllCascade = (selected: boolean): void => {
    setCascade((prev) => prev.map((c) => ({ ...c, selected })));
  };

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    setSubmitting(true);
    setErr(null);
    const selected = cascade.filter((c) => c.selected);
    const verLabel =
      VERIFICATION_METHODS.find((v) => v.code === verCode)?.label ?? verCode;
    const result = await run({
      cifId,
      newAddr: addr.trim(),
      newAddr2: addr2.trim() || undefined,
      newCity: city.trim(),
      newState: state.trim().toUpperCase(),
      newZip: zip.trim(),
      verificationCode: verCode,
      verificationLabel: verLabel,
      customerInitials: initials.trim(),
      officerCode,
      cascadeAccounts: selected.map((s) => ({ acctId: s.acctId, acctType: s.acctType })),
      outOfState,
    });
    setSubmitting(false);
    if (!result.ok) {
      setErr(result.error ?? 'Unknown error');
      return;
    }
    onSaved?.();
    onClose();
  };

  if (!open) return null;

  return (
    <ModalShell
      open={open}
      onClose={submitting ? () => { /* block close */ } : onClose}
      title="Change Address"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          {!readOnly && (
            <Button variant="primary" onClick={() => { void handleSave(); }} disabled={!canSave}>
              {submitting ? 'Saving…' : 'Save Address'}
            </Button>
          )}
        </>
      }
    >
      {readOnly && (
        <Banner tone="warn" title="Read-only">
          You do not have the Address-Maintenance grant. Close and escalate to a supervisor.
        </Banner>
      )}

      {err && (
        <Banner tone="err" title="Save failed">{err}</Banner>
      )}

      {outOfState && (
        <Banner tone="warn" title="Out-of-state move detected">
          A compliance task will be auto-created on save. CIP re-verification may be required.
        </Banner>
      )}

      <div>
        <label style={labelStyle}>Street Address *</label>
        <input
          type="text"
          value={addr}
          onChange={(e) => { setAddr(e.target.value); }}
          disabled={readOnly || submitting}
          style={inputStyle}
          autoFocus
        />
      </div>
      <div>
        <label style={labelStyle}>Address Line 2</label>
        <input
          type="text"
          value={addr2}
          onChange={(e) => { setAddr2(e.target.value); }}
          disabled={readOnly || submitting}
          style={inputStyle}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 'var(--sp-4)' }}>
        <div>
          <label style={labelStyle}>City *</label>
          <input
            type="text"
            value={city}
            onChange={(e) => { setCity(e.target.value); }}
            disabled={readOnly || submitting}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>State *</label>
          <input
            type="text"
            maxLength={2}
            value={state}
            onChange={(e) => { setState(e.target.value.toUpperCase()); }}
            disabled={readOnly || submitting}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Zip *</label>
          <input
            type="text"
            value={zip}
            onChange={(e) => { setZip(e.target.value); }}
            disabled={readOnly || submitting}
            style={inputStyle}
          />
        </div>
      </div>

      <div>
        <label style={labelStyle}>Verification Method *</label>
        <select
          aria-label="Verification Method"
          value={verCode}
          onChange={(e) => { setVerCode(e.target.value); }}
          disabled={readOnly || submitting}
          style={inputStyle}
        >
          {VERIFICATION_METHODS.map((v) => (
            <option key={v.code} value={v.code}>{v.label}</option>
          ))}
        </select>
      </div>
      {verCode !== '0' && verCode !== '3' && (
        <div>
          <label style={labelStyle}>Customer Initials * (min 2 chars)</label>
          <input
            type="text"
            value={initials}
            onChange={(e) => { setInitials(e.target.value.toUpperCase()); }}
            disabled={readOnly || submitting}
            maxLength={4}
            style={{ ...inputStyle, width: 120 }}
          />
        </div>
      )}

      {cascade.length > 0 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>
              Cascade to related accounts ({cascade.filter((c) => c.selected).length}/{cascade.length})
            </label>
            <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
              <Button variant="link" size="sm" onClick={() => selectAllCascade(true)} disabled={readOnly || submitting}>
                Select all
              </Button>
              <Button variant="link" size="sm" onClick={() => selectAllCascade(false)} disabled={readOnly || submitting}>
                Clear
              </Button>
            </div>
          </div>
          <div style={{ border: '1px solid var(--ink-100)', borderRadius: 'var(--r-sm)', maxHeight: 180, overflowY: 'auto' }}>
            {cascade.map((c) => (
              <label
                key={c.acctId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-3)',
                  padding: '6px 10px',
                  borderBottom: '1px solid var(--ink-50)',
                  fontSize: 13,
                  cursor: readOnly || submitting ? 'not-allowed' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={c.selected}
                  onChange={() => { toggleCascade(c.acctId); }}
                  disabled={readOnly || submitting}
                />
                <code style={{ fontFamily: 'var(--font-mono)', width: 110 }}>{c.acctId}</code>
                <span style={{ color: 'var(--ink-600)', width: 60 }}>
                  {ACCT_TYPE_LABEL[String(c.acctType)] ?? String(c.acctType)}
                </span>
                <span style={{ color: 'var(--ink-500)', flex: 1 }}>{c.prodDesc ?? ''}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* TODO: wire disclosure packet when Team 2's engine lands
          (Change-of-Contact form + updated signature card). */}
    </ModalShell>
  );
}
